import type { ExportModelInput, ModelArtifacts } from './trainer';
import { getTf } from './tfBackend';

/**
 * Shared `model.json + weights.bin` extractor for every trainer that
 * persists a TF.js LayersModel to IndexedDB.
 *
 * Pulls the saved model out via TF.js's IO handler API, captures
 * the bytes the SaveHandler is fed, stitches the weights buffers
 * back into a single Uint8Array, and returns a `ModelArtifacts`
 * payload the publish + PWA pipelines can consume.
 *
 * Per-trainer differences live in `extras` (metadata sidecar fields
 * the runtime needs — e.g. spectrogram dimensions for audio, the
 * project kind so the runtime can pick the right inference path).
 */
export async function extractTfjsLayersModelArtifacts({
  modelKey,
  input,
  extras = {},
}: {
  modelKey: string;
  input: ExportModelInput;
  /** Trainer-specific metadata merged into `userDefinedMetadata` and
   *  the metadataJson sidecar. Keep keys prefixed with `bryta` so
   *  third-party tools recognising plain TF.js models still see
   *  unscoped fields they expect. */
  extras?: Record<string, unknown>;
}): Promise<ModelArtifacts> {
  const tf = await getTf();
  type Tf = typeof import('@tensorflow/tfjs');
  let head: Awaited<ReturnType<Tf['loadLayersModel']>>;
  try {
    head = await tf.loadLayersModel(modelKey);
  } catch {
    throw new Error('No trained model was found to export.');
  }
  try {
    const userMetadata = {
      brytaProjectName: input.projectName,
      brytaLabels: input.labels,
      brytaSchemaVersion: 1,
      ...extras,
    };
    head.setUserDefinedMetadata(userMetadata);

    let captured: import('@tensorflow/tfjs').io.ModelArtifacts | null = null;
    const saveHandler: import('@tensorflow/tfjs').io.IOHandler = {
      save: async (artifacts) => {
        captured = artifacts;
        let weightDataBytes = 0;
        if (artifacts.weightData) {
          if (Array.isArray(artifacts.weightData)) {
            for (const buf of artifacts.weightData) weightDataBytes += buf.byteLength;
          } else {
            weightDataBytes = artifacts.weightData.byteLength;
          }
        }
        return {
          modelArtifactsInfo: {
            dateSaved: new Date(),
            modelTopologyType: 'JSON',
            modelTopologyBytes:
              typeof artifacts.modelTopology === 'string'
                ? artifacts.modelTopology.length
                : JSON.stringify(artifacts.modelTopology ?? {}).length,
            weightDataBytes,
            weightSpecsBytes: JSON.stringify(artifacts.weightSpecs ?? []).length,
          },
        };
      },
    };
    await head.save(saveHandler);
    if (!captured) {
      throw new Error('TF.js did not produce model artifacts.');
    }
    const artifacts = captured as import('@tensorflow/tfjs').io.ModelArtifacts;

    const modelJson = {
      modelTopology: artifacts.modelTopology,
      format: artifacts.format ?? 'layers-model',
      generatedBy: 'bryta-learn',
      convertedBy: null,
      weightsManifest: [{ paths: ['./weights.bin'], weights: artifacts.weightSpecs ?? [] }],
      userDefinedMetadata: artifacts.userDefinedMetadata ?? userMetadata,
    };

    const weightBuffers: ArrayBuffer[] = [];
    if (artifacts.weightData) {
      if (Array.isArray(artifacts.weightData)) {
        for (const buf of artifacts.weightData) weightBuffers.push(buf);
      } else {
        weightBuffers.push(artifacts.weightData);
      }
    }
    const totalWeightBytes = weightBuffers.reduce((sum, b) => sum + b.byteLength, 0);
    const weightsBin = new Uint8Array(totalWeightBytes);
    let weightOffset = 0;
    for (const buf of weightBuffers) {
      weightsBin.set(new Uint8Array(buf), weightOffset);
      weightOffset += buf.byteLength;
    }

    return {
      modelJson,
      weightsBin,
      metadataJson: {
        brytaProjectName: input.projectName,
        brytaLabels: input.labels,
        brytaSchemaVersion: 1,
        exportedAt: new Date().toISOString(),
        ...extras,
      },
    };
  } finally {
    head.dispose();
  }
}
